import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { Alert, Button } from '../common/UIComponents';
import { useAuth } from '../../contexts/AuthContext';
import { functions } from '../../firebase';
import { isAdmin } from '../../utils/accessControl';

interface GenerateTestServiceProvidersResponse {
  success: boolean;
  createdCount: number;
  providersWithServiceCapabilities?: number;
  providersWithPublicHourlyRate: number;
  providersWithBusinessHours: number;
  generatedReviews: number;
}

type FeedbackVariant = 'success' | 'error';

const generatorLimits = {
  min: 1,
  max: 50,
};

const TestDataProviderGenerator: React.FC = () => {
  const { userProfile, claims, loading, currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [numberOfProviders, setNumberOfProviders] = useState('10');
  const [feedback, setFeedback] = useState<{ variant: FeedbackVariant; message: string } | null>(
    null
  );
  const [summary, setSummary] = useState<GenerateTestServiceProvidersResponse | null>(null);

  if (loading) {
    return <p className="text-sm text-primary-light-400">Ładowanie uprawnień admina...</p>;
  }

  if (!isAdmin(userProfile, claims)) {
    return null;
  }

  const generateTestServiceProvidersCallable = httpsCallable<
    { numberOfProviders: number },
    GenerateTestServiceProvidersResponse
  >(functions, 'generateTestServiceProviders');

  const parseRequestedCount = (rawValue: string): number | null => {
    const parsed = Number.parseInt(rawValue, 10);
    if (Number.isNaN(parsed)) {
      return null;
    }
    if (parsed < generatorLimits.min || parsed > generatorLimits.max) {
      return null;
    }
    return parsed;
  };

  const handleGenerate = async (count: number) => {
    if (!Number.isInteger(count) || count < generatorLimits.min || count > generatorLimits.max) {
      setSummary(null);
      setFeedback({
        variant: 'error',
        message: 'Podaj liczbę profili testowych w zakresie 1-50.',
      });
      return;
    }

    setIsLoading(true);
    setFeedback(null);
    setSummary(null);

    try {
      if (currentUser) {
        await currentUser.getIdToken(true);
      }

      const result = await generateTestServiceProvidersCallable({ numberOfProviders: count });
      if (!result.data?.success) {
        throw new Error('Generator zwrócił niepełną odpowiedź.');
      }

      setSummary(result.data);
      setFeedback({
        variant: 'success',
        message: `Wygenerowano ${result.data.createdCount} testowych profili serwisów.`,
      });
    } catch (error: any) {
      const code = String(error?.code || '');
      let message = 'Nie udało się wygenerować testowych profili serwisów.';

      if (code.includes('permission-denied')) {
        message = 'Brak uprawnień do wygenerowania danych testowych.';
      } else if (code.includes('failed-precondition')) {
        message =
          error?.message ||
          'Brakuje słownika Smart Search albo aktywnego katalogu compat potrzebnego do generatora testowego.';
      } else if (code.includes('deadline-exceeded')) {
        message = 'Operacja trwała zbyt długo. Spróbuj wygenerować mniejszą paczkę danych.';
      } else if (code.includes('internal') || code.includes('unavailable')) {
        message = 'Generator jest chwilowo niedostępny. Spróbuj ponownie za chwilę.';
      } else if (error?.message) {
        message = error.message;
      }

      setFeedback({
        variant: 'error',
        message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const parsedRequestedCount = parseRequestedCount(numberOfProviders);
  const isMultiGenerateDisabled = isLoading || parsedRequestedCount === null;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button
            variant="primary"
            size="md"
            onClick={() => handleGenerate(1)}
            isLoading={isLoading}
            disabled={isLoading}
          >
            Wygeneruj 1 profil testowy
          </Button>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <label htmlFor="admin-test-provider-count" className="text-sm text-primary-light-300">
              Wiele naraz
            </label>
            <input
              id="admin-test-provider-count"
              type="number"
              min={generatorLimits.min}
              max={generatorLimits.max}
              inputMode="numeric"
              value={numberOfProviders}
              onChange={(event) => setNumberOfProviders(event.target.value)}
              disabled={isLoading}
              className="w-full rounded-lg border border-primary-dark-300/50 bg-primary-dark-100/24 px-3 py-2 text-sm text-primary-light-100 outline-none transition-colors focus:border-ratuj-300 sm:w-28"
            />
            <Button
              variant="neutral"
              size="md"
              onClick={() => handleGenerate(parsedRequestedCount ?? 0)}
              isLoading={isLoading}
              disabled={isMultiGenerateDisabled}
            >
              Generuj paczkę
            </Button>
          </div>
        </div>

        <p className="text-xs leading-relaxed text-primary-light-400">
          Jedno uruchomienie tworzy od 1 do 50 profili testowych ze Smart Search V1.
        </p>
      </div>

      {feedback && (
        <Alert variant={feedback.variant === 'success' ? 'success' : 'error'}>
          {feedback.message}
        </Alert>
      )}

      {summary && (
        <div className="rounded-lg border border-primary-dark-300/30 bg-primary-dark-100/18 p-3 sm:p-4">
          <p className="text-xs uppercase tracking-wide text-primary-light-400">
            Podsumowanie generatora
          </p>
          <dl className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <div>
              <dt className="text-xs text-primary-light-400">Profile utworzone</dt>
              <dd className="mt-1 text-base font-semibold text-primary-light-100">
                {summary.createdCount}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-primary-light-400">Zakres Smart Search</dt>
              <dd className="mt-1 text-base font-semibold text-primary-light-100">
                {summary.providersWithServiceCapabilities ?? summary.createdCount}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-primary-light-400">Profile ze stawką</dt>
              <dd className="mt-1 text-base font-semibold text-primary-light-100">
                {summary.providersWithPublicHourlyRate}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-primary-light-400">Profile z godzinami</dt>
              <dd className="mt-1 text-base font-semibold text-primary-light-100">
                {summary.providersWithBusinessHours}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-primary-light-400">Wygenerowane opinie</dt>
              <dd className="mt-1 text-base font-semibold text-primary-light-100">
                {summary.generatedReviews}
              </dd>
            </div>
          </dl>
        </div>
      )}
    </div>
  );
};

export default TestDataProviderGenerator;
